import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users, Radar as RadarIcon, LayoutGrid, GraduationCap, ChevronRight, ChevronLeft, Eye, X, GitBranch } from 'lucide-react';
import { fetchRfInboxRequest, fetchConsolidatedResultsRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { fetchAllFormationSkillsRequest } from '../../../redux/actions/formationSkillActions';
import { fetchDomainesRequest, fetchThemesRequest } from '../../../redux/actions/paramDomainesThemesActions';
import { getAllCareerPathsRequest } from '../../../redux/actions/careerPathActions';
import { getAllEmployeeCareerPathsRequest } from '../../../redux/actions/employeeCareerPathActions';
import { fetchSeuilPassageRequest } from '../../../redux/actions/seuilPassageActions';
import { getAllJobsRequest } from '../../../redux/actions/Jobactions';
import SkillRadar from '../components/SkillRadar';
import Team4Box from '../components/Team4Box';
import { buildDomainThemeRadarOptions, buildRadarFilterOptions, filterRadarData, filterUsersByRadarFilters, getUserEntity, getUserPoste, isSkillsCampaign, RadarFiltersPanel, toRadarSkill } from '../radarFilters';
import '../EvaluationDesign.css';

const normalizeText = (value) => (value || "").toString().toLowerCase().trim();

const getSortedSteps = (careerPath) =>
    [...(careerPath?.steps || [])].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

const getJobName = (job) => job?.name || job?.nom || job?.label || "Poste inconnu";
const getJobCode = (job) => job?.code || job?.reference || "";
const getUserMatricule = (user) =>
    user?.matricule || user?.employeeCode || user?.userEmployeeCode || user?.registrationNumber || "";
const getCompetenceLevel = (level) =>
    Number(level?.scoreObtenu ?? level?.scoreRf ?? level?.scoreN2 ?? level?.scoreManager ?? level?.scoreCollab ?? 0);

const getAverageCompetenceLevel = (user) => {
    const scores = (user?.niveaux || [])
        .map(getCompetenceLevel)
        .filter((score) => Number.isFinite(score));
    if (!scores.length) return 0;
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
};

const buildAiRoleSuggestions = (user, jobs = [], careerPaths = [], employeeCareerPaths = []) => {
    const currentPoste = getUserPoste(user);
    const average = getAverageCompetenceLevel(user);
    const baseScore = Math.min(88, Math.max(28, Math.round((average / 4) * 78)));
    const { paths } = getRoadmapPathsForUser(careerPaths, employeeCareerPaths, user);
    const pathJobNames = new Set();

    (paths || []).forEach((assignment) => {
        const careerPath = assignment?.careerPath || assignment;
        getSortedSteps(careerPath).forEach((step) => {
            pathJobNames.add(normalizeText(getJobName(step.job)));
        });
    });

    const companyJobNames = (jobs || [])
        .map((job) => getJobName(job))
        .filter(Boolean)
        .filter((name) => normalizeText(name) !== normalizeText(currentPoste))
        .filter((name) => !pathJobNames.has(normalizeText(name)));

    const namesToScore = Array.from(new Set(companyJobNames));
    const fallbackNames = ['Directeur RH', 'Directeur financier', 'Responsable operations'];
    const sourceNames = namesToScore.length ? namesToScore : fallbackNames;

    return sourceNames.slice(0, 5).map((name, index) => ({
        name,
        score: Math.max(10, Math.min(95, baseScore - index * 12 + (index === 0 ? 8 : 0))),
    }));
};

const isSameJob = (job, role) => {
    if (!job || !role) return false;
    const normalizedRole = normalizeText(role);
    const name = normalizeText(getJobName(job));
    const code = normalizeText(getJobCode(job));
    return name === normalizedRole || code === normalizedRole || normalizedRole.includes(name) || (code && normalizedRole.includes(code));
};

const toGlobalTab = (tab) => ({
    radar: 'radars',
    '4box': '4box',
    plan: 'plans',
    roadmap: 'roadmaps',
}[tab] || 'radars');

const toIndividualTab = (tab) => ({
    radars: 'radar',
    '4box': '4box',
    plans: 'plan',
    roadmaps: 'roadmap',
}[tab] || 'radar');

const findCurrentStepIndex = (steps, userRole, currentStep) => {
    const currentStepId = currentStep?.id;
    const byCurrentStep = currentStepId ? steps.findIndex(step => step.id === currentStepId) : -1;
    if (byCurrentStep >= 0) return byCurrentStep;
    return steps.findIndex(step => isSameJob(step.job, userRole));
};

const findSeuilForTransition = (seuils, fromJob, toJob) => {
    if (!fromJob || !toJob) return null;
    const fromId = fromJob.id;
    const toId = toJob.id;
    const fromName = normalizeText(getJobName(fromJob));
    const toName = normalizeText(getJobName(toJob));
    const fromCode = normalizeText(getJobCode(fromJob));
    const toCode = normalizeText(getJobCode(toJob));

    return (seuils || []).find(seuil => {
        const originMatches = (fromId && seuil.posteOrigineId === fromId)
            || normalizeText(seuil.posteOrigineName) === fromName
            || (fromCode && normalizeText(seuil.posteOrigineCode) === fromCode);
        const targetMatches = (toId && seuil.posteCibleId === toId)
            || normalizeText(seuil.posteCibleName) === toName
            || (toCode && normalizeText(seuil.posteCibleCode) === toCode);
        return originMatches && targetMatches;
    });
};

const getUserCareerAssignments = (employeeCareerPaths, user) => {
    const userId = user?.userId || user?.id;
    const userName = normalizeText(user?.nom || user?.userFullName);
    return (employeeCareerPaths || []).filter(ecp =>
        (userId && ecp.userId === userId) || (userName && normalizeText(ecp.userName) === userName)
    );
};

const getRoadmapPathsForUser = (careerPaths, employeeCareerPaths, user) => {
    const userRole = getUserPoste(user);
    const assignedPaths = getUserCareerAssignments(employeeCareerPaths, user);
    const fallbackPaths = (careerPaths || [])
        .filter(cp => cp.steps?.some(step => isSameJob(step.job, userRole)))
        .map(cp => ({ id: `fallback-${user?.id}-${cp.id}`, careerPath: cp, currentStep: null, inferred: true }));

    return {
        paths: assignedPaths.length > 0 ? assignedPaths : fallbackPaths,
        inferred: assignedPaths.length === 0 && fallbackPaths.length > 0,
    };
};

const getNextRoadmapJobName = (user, careerPaths, employeeCareerPaths) => {
    const userRole = getUserPoste(user);
    const { paths } = getRoadmapPathsForUser(careerPaths, employeeCareerPaths, user);

    for (const item of paths) {
        const careerPath = item?.careerPath || item;
        const steps = getSortedSteps(careerPath);
        const currentIndex = findCurrentStepIndex(steps, userRole, item?.currentStep);
        const nextStep = currentIndex >= 0 ? steps[currentIndex + 1] : null;
        if (nextStep) return getJobName(nextStep.job);
    }

    return null;
};

const splitUserName = (user) => {
    const fullName = user?.nom || user?.userFullName || '';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return {
        firstName: user?.prenom || user?.firstName || user?.givenName || (parts.length > 1 ? parts.slice(0, -1).join(' ') : fullName),
        lastName: user?.nomFamille || user?.lastName || user?.familyName || (parts.length > 1 ? parts[parts.length - 1] : ''),
    };
};

const getUserSeniority = (user) =>
    user?.anciennete || user?.seniority || user?.yearsOfExperience || user?.anneesExperience || user?.experience || '';

const UserTrainingPlanView = ({ radarData, allMappings }) => {
    return (
        <div className="eval-plan-container">
            <div className="eval-plan-header">
                <h3><GraduationCap size={18} /> Plan de Formation Personnalisé</h3>
                <p>Formations recommandées pour combler les écarts de compétences identifiés.</p>
            </div>

            <div className="eval-plan-list">
                {radarData.filter(s => s.requis > s.obtenu).length > 0 ? (
                    radarData.filter(s => s.requis > s.obtenu).map(skill => {
                        const recommendations = allMappings.filter(m => m.skillId === skill.id);
                        const gap = skill.requis - skill.obtenu;
                        const labelsList = ["Basique", "Intermédiaire", "Avancé", "Confirmé", "Expert"];
                        const maxSteps = Math.min(Math.max(skill.requis, 1), 5); 
                        const stepLabels = labelsList.slice(0, maxSteps);
                        
                        return (
                            <div key={skill.id} className="eval-plan-card-modern">
                                <div className="eval-plan-skill-title">{skill.skill}</div>
                                
                                <div className="eval-stepper">
                                    {stepLabels.map((lbl, idx) => {
                                        const isCompleted = idx < Math.floor(skill.obtenu);
                                        const isLineGreen = (idx + 1) < Math.floor(skill.obtenu);
                                        const isLast = idx === stepLabels.length - 1;
                                        
                                        return (
                                            <React.Fragment key={idx}>
                                                <div className="eval-step-node">
                                                    <div className={`eval-step-circle ${isCompleted ? 'completed' : ''}`}>
                                                        {idx + 1}
                                                    </div>
                                                    <div className={`eval-step-label ${isCompleted ? 'completed' : ''}`}>
                                                        {lbl}
                                                    </div>
                                                </div>
                                                {!isLast && (
                                                    <div className={`eval-step-bar ${isLineGreen ? 'completed' : ''}`}></div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>

                                <div className="eval-plan-recommendations-section">
                                    <div className="eval-plan-rec-title">Formations nécessaires</div>
                                    
                                    {recommendations.length > 0 ? recommendations.map(rec => (
                                        <div key={rec.id} className="eval-rec-row">
                                            <div className="eval-rec-name">{rec.formationNom}</div>
                                            <div className="eval-rec-progress">
                                                <svg viewBox="0 0 36 36" className="circular-chart blue">
                                                    <path className="circle-bg"
                                                        d="M18 2.0845
                                                        a 15.9155 15.9155 0 0 1 0 31.831
                                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                    <path className="circle"
                                                        strokeDasharray={`${rec.pourcentage}, 100`}
                                                        d="M18 2.0845
                                                        a 15.9155 15.9155 0 0 1 0 31.831
                                                        a 15.9155 15.9155 0 0 1 0 -31.831"
                                                    />
                                                    <text x="18" y="20.35" className="percentage">{rec.pourcentage}%</text>
                                                </svg>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="eval-rec-empty">
                                            Aucune formation associée à cette compétence.
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="eval-plan-completed">
                        <div className="eval-success-icon">✓</div>
                        <h4>Félicitations !</h4>
                        <p>Ce collaborateur possède ou dépasse tous les niveaux requis pour son poste.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const CollaboratorRoadmapView = ({ selectedUser, careerPaths, employeeCareerPaths }) => {
    const userRole = selectedUser?.poste || "";
    const { paths: pathsToDisplay } = getRoadmapPathsForUser(careerPaths, employeeCareerPaths, selectedUser);

    return (
        <div className="eval-plan-container">
            {/*<div className="eval-plan-header">*/}
            {/*    <h3><GitBranch size={18} /> Parcours de carrière </h3>*/}
            {/*    <p>Parcours professionnels et seuils de passage pour <strong>{selectedUser?.nom}</strong>.</p>*/}
            {/*</div>*/}
            <div className="eval-plan-list">
                {pathsToDisplay.length > 0 ? pathsToDisplay.map(item => (
                    <RoadmapPathCard
                        key={item.id || item.careerPath?.id}
                        assignment={item}
                        userRole={userRole}
                        user={selectedUser}
                    />
                )) : (
                    <div className="eval-plan-completed" style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                        <div className="eval-success-icon" style={{ backgroundColor: '#94a3b8' }}><GitBranch size={24} /></div>
                        <h4 style={{ color: '#475569' }}>Aucun parcours dÃ©fini</h4>
                        <p style={{ color: '#64748b' }}>Aucune roadmap n'est associÃ©e au poste de {userRole}.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const RoadmapAiAnalysisView = ({ selectedUser, careerPaths, employeeCareerPaths, jobs }) => {
    const suggestions = buildAiRoleSuggestions(selectedUser, jobs, careerPaths, employeeCareerPaths);
    const currentPoste = getUserPoste(selectedUser) || 'Poste actuel';
    const { paths } = getRoadmapPathsForUser(careerPaths, employeeCareerPaths, selectedUser);
    const configuredSteps = getSortedSteps(paths?.[0]?.careerPath || paths?.[0] || {})
        .map((step) => getJobName(step.job))
        .filter(Boolean);

    return (
        <div className="eval-rf-ai-analysis">
            <div className="eval-rf-ai-head">
                <h3>Analyse prescriptive IA</h3>
                <p>Explorez les possibilités d'évolution proposées à partir des postes existants dans l'entreprise.</p>
            </div>

            <RoadmapHorizontalPath
                className="eval-rf-ai-career-path"
                steps={(configuredSteps.length ? configuredSteps : [currentPoste]).map((name) => ({
                    key: name,
                    name,
                    isCurrent: normalizeText(name) === normalizeText(currentPoste),
                }))}
            />

            <div className="eval-rf-ai-suggestions">
                {suggestions.length > 0 ? suggestions.map((item) => (
                    <div key={item.name} className="eval-rf-ai-suggestion-card">
                        <strong>{item.name}</strong>
                        <span>{item.score}%</span>
                    </div>
                )) : (
                    <div className="eval-rf-ai-empty">
                        Aucun poste cible disponible pour l'analyse IA.
                    </div>
                )}
            </div>
        </div>
    );
};

const RoadmapEligibilityModal = ({ currentJob, nextJob, average, onClose }) => {
    const eligible = average > 2;

    return (
        <div className="eval-rf-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="eval-rf-modal eval-rf-eligibility-modal">
                <button className="eval-rf-modal-close" type="button" onClick={onClose}>
                    <X size={18} />
                </button>
                <h3>{currentJob} &rarr; {nextJob || 'Poste suivant'}</h3>
                <div className="eval-rf-eligibility-box">
                    <strong>Moyenne des compétences : {average.toFixed(2)}/4</strong>
                    <p className={eligible ? 'eligible' : 'not-eligible'}>
                        {eligible
                            ? "✓ L'employé est éligible pour passer au poste suivant"
                            : "✕ L'employé n'est pas éligible pour passer au poste suivant"}
                    </p>
                </div>
            </div>
        </div>
    );
};

const RoadmapHorizontalPath = ({ steps, className = '' }) => (
    <div className={`eval-rf-roadmap-horizontal ${className}`}>
        {steps.map((step, index) => {
            const content = (
                <>
                    <div className="eval-rf-roadmap-step-top">
                        <div className={`eval-rf-roadmap-step-index ${step.isCurrent ? 'current' : ''}`} />
                        {index < steps.length - 1 && <div className="eval-rf-roadmap-connector" />}
                    </div>
                    <div className={`eval-rf-roadmap-step-card ${step.isCurrent ? 'current clickable' : ''}`}>
                        <div className="eval-rf-roadmap-step-head">
                            <strong>{step.name}</strong>
                            {step.isCurrent && <span>Poste actuel</span>}
                        </div>
                    </div>
                </>
            );

            return step.onClick ? (
                <button key={step.key} type="button" className="eval-rf-roadmap-step-wrap as-button" onClick={step.onClick}>
                    {content}
                </button>
            ) : (
                <div key={step.key} className="eval-rf-roadmap-step-wrap">
                    {content}
                </div>
            );
        })}
    </div>
);

const RoadmapPathCard = ({ assignment, userRole, user }) => {
    const careerPath = assignment?.careerPath || assignment;
    const sortedSteps = getSortedSteps(careerPath);
    const currentStepIndex = findCurrentStepIndex(sortedSteps, userRole, assignment?.currentStep);
    const [eligibilityModal, setEligibilityModal] = useState(null);
    const averageCompetenceLevel = getAverageCompetenceLevel(user);

    return (
        <div className="eval-plan-card-modern" style={{ marginBottom: '20px' }}>
            <div className="eval-plan-skill-title" style={{ fontSize: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '18px' }}>
                {careerPath?.name || 'Parcours sans nom'}
                {assignment?.status && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: '999px' }}>
                        {assignment.status}
                    </span>
                )}
            </div>
            <RoadmapHorizontalPath
                steps={sortedSteps.map((step, idx) => {
                    const isCurrent = idx === currentStepIndex;
                    return {
                        key: step.id || idx,
                        name: getJobName(step.job),
                        isCurrent,
                        onClick: isCurrent ? () => setEligibilityModal({
                            currentJob: getJobName(step.job),
                            nextJob: sortedSteps[idx + 1] ? getJobName(sortedSteps[idx + 1].job) : null,
                        }) : null,
                    };
                })}
            />
            {eligibilityModal && (
                <RoadmapEligibilityModal
                    currentJob={eligibilityModal.currentJob}
                    nextJob={eligibilityModal.nextJob}
                    average={averageCompetenceLevel}
                    onClose={() => setEligibilityModal(null)}
                />
            )}
        </div>
    );
};

const RoadmapThreshold = ({ seuil }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '9px' }}>
        <span style={{ fontSize: '11px', color: '#334155', background: '#e0f2fe', border: '1px solid #bae6fd', padding: '3px 8px', borderRadius: '999px' }}>
            Seuil global {seuil.seuilGlobalMatch}%
        </span>
        {seuil.anneesExperienceRequises != null && (
            <span style={{ fontSize: '11px', color: '#334155', background: '#fef3c7', border: '1px solid #fde68a', padding: '3px 8px', borderRadius: '999px' }}>
                {seuil.anneesExperienceRequises} an(s) experience
            </span>
        )}
        {seuil.validationManagerRequise && (
            <span style={{ fontSize: '11px', color: '#334155', background: '#ede9fe', border: '1px solid #ddd6fe', padding: '3px 8px', borderRadius: '999px' }}>
                Validation manager
            </span>
        )}
    </div>
);

// eslint-disable-next-line no-unused-vars
const RoadmapsDashboard = ({ users, careerPaths, employeeCareerPaths, seuils, onUserSelect }) => {
    const rows = users.map(user => {
        const { paths, inferred } = getRoadmapPathsForUser(careerPaths, employeeCareerPaths, user);
        return {
            user,
            paths,
            inferred
        };
    });

    return (
        <div className="eval-general-roadmaps" style={{ marginTop: '20px', textAlign: 'left' }}>
            {/*<div style={{ marginBottom: '20px', color: '#64748b' }}>*/}
            {/*    Parcours de carriÃ¨re par collaborateur, avec le poste actuel, la prochaine Ã©tape et les seuils de passage.*/}
            {/*</div>*/}
            <div style={{ display: 'grid', gap: '16px' }}>
                {rows.length > 0 ? rows.map(({ user, paths, inferred }) => (
                    <div key={user.id} className="eval-plan-card-modern">
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '14px' }}>
                            <div>
                                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>{user.nom}</div>
                                <div style={{ color: '#64748b', fontSize: '13px', marginTop: '2px' }}>{user.poste}</div>
                            </div>
                            <button
                                onClick={() => onUserSelect(user)}
                                style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                                title="Voir la roadmap detaillee"
                            >
                                <Eye size={14} /> Voir
                            </button>
                        </div>
                        {paths.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                                {paths.map(path => (
                                    <RoadmapMiniPath key={path.id || path.careerPath?.id} assignment={path} userRole={user.poste} seuils={seuils} inferred={inferred} />
                                ))}
                            </div>
                        ) : (
                            <div className="eval-empty-small" style={{ border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '14px' }}>
                                Aucun parcours associe a ce collaborateur.
                            </div>
                        )}
                    </div>
                )) : (
                    <div className="eval-empty-small">Aucun collaborateur a afficher.</div>
                )}
            </div>
        </div>
    );
};

const RoadmapSelectCollaboratorMessage = () => (
    <div className="eval-rf-roadmap-empty">
        <GitBranch size={26} />
        <h3>Veuillez sélectionner un collaborateur</h3>
        <p>Utilisez le champ de recherche par nom ou matricule, puis choisissez le collaborateur à afficher.</p>
    </div>
);

const RoadmapMiniPath = ({ assignment, userRole, seuils, inferred }) => {
    const careerPath = assignment?.careerPath || assignment;
    const steps = getSortedSteps(careerPath);
    const currentIndex = findCurrentStepIndex(steps, userRole, assignment?.currentStep);
    const currentStep = currentIndex >= 0 ? steps[currentIndex] : null;
    const nextStep = currentIndex >= 0 ? steps[currentIndex + 1] : null;
    const seuil = currentStep && nextStep ? findSeuilForTransition(seuils, currentStep.job, nextStep.job) : null;

    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#f8fafc' }}>
            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                {careerPath?.name || 'Parcours sans nom'}
                {inferred && <span style={{ marginLeft: '6px', color: '#64748b', fontWeight: 500 }}>(deduit)</span>}
            </div>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '12px', flexWrap: 'wrap' }}>
                <span>{currentStep ? getJobName(currentStep.job) : userRole}</span>
                {nextStep && <ChevronRight size={14} />}
                {nextStep && <strong style={{ color: '#0f172a' }}>{getJobName(nextStep.job)}</strong>}
            </div>
            {/*{seuil && <RoadmapThreshold seuil={seuil} />}*/}
            {!nextStep && <div style={{ color: '#64748b', fontSize: '12px', marginTop: '8px' }}>Dernière étape du parcours.</div>}
        </div>
    );
};

const DetailValue = ({ label, value }) => (
    <div className="eval-rf-detail-value">
        <span>{label}</span>
        <strong>{value || '-'}</strong>
    </div>
);

const CollaboratorDetailModal = ({ user, onClose, onShowRoadmap }) => {
    if (!user) return null;
    const { firstName, lastName } = splitUserName(user);

    return (
        <div className="eval-rf-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="eval-rf-modal eval-rf-detail-modal">
                <button className="eval-rf-modal-close" type="button" onClick={onClose}>
                    <X size={18} />
                </button>
                <div className="eval-rf-modal-head">
                    <div className="eval-rf-modal-avatar">{user.initiales || (user.nom || '?').slice(0, 2).toUpperCase()}</div>
                    <div>
                        <h3>{user.nom || user.userFullName || 'Collaborateur'}</h3>
                        <p>{getUserPoste(user) || 'Poste non renseigne'}</p>
                    </div>
                </div>

                <div className="eval-rf-detail-grid">
                    <DetailValue label="Nom" value={lastName || user.nom || user.userFullName} />
                    <DetailValue label="Prenom" value={firstName} />
                    <DetailValue label="Poste" value={getUserPoste(user)} />
                    <DetailValue label="Manager" value={user.managerName || user.managerFullName || user.managerDecisionByName} />
                    <DetailValue label="Entite" value={getUserEntity(user)} />
                    <DetailValue label="Anciennete" value={getUserSeniority(user)} />
                </div>

                <div className="eval-rf-modal-actions">
                    <button className="eval-rf-modal-secondary" type="button" onClick={onClose}>Fermer</button>
                    <button className="eval-rf-modal-primary" type="button" onClick={() => onShowRoadmap(user)}>
                        <GitBranch size={14} /> Voir dans roadmap
                    </button>
                </div>
            </div>
        </div>
    );
};

const CollaboratorRoadmapModal = ({ user, careerPaths, employeeCareerPaths, onClose }) => {
    if (!user) return null;
    const nextJob = getNextRoadmapJobName(user, careerPaths, employeeCareerPaths);

    return (
        <div className="eval-rf-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
            <div className="eval-rf-modal eval-rf-roadmap-modal">
                <button className="eval-rf-modal-close" type="button" onClick={onClose}>
                    <X size={18} />
                </button>
                <div className="eval-rf-roadmap-title">
                    <div>
                        <h3>Roadmap - {user.nom || user.userFullName}</h3>
                        <p>{getUserPoste(user) || 'Poste non renseigne'}</p>
                    </div>
                    <div className="eval-rf-next-job">
                        <span>Prochain poste</span>
                        <strong>{nextJob || 'Aucun prochain poste identifie'}</strong>
                    </div>
                </div>
                <CollaboratorRoadmapView
                    selectedUser={user}
                    careerPaths={careerPaths}
                    employeeCareerPaths={employeeCareerPaths}
                />
            </div>
        </div>
    );
};

export default function RfRadarPage() {
    const dispatch = useDispatch();
    const { user: currentUser } = useSelector(state => state.auth || {});
    const { inboxRf, consolidatedResults, loading } = useSelector(state => state.skillEvaluation || { inboxRf: [], consolidatedResults: [] });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { allMappings } = useSelector(state => state.formationSkill || { allMappings: [] });
    const { domaines = [], themes = [] } = useSelector(state => state.paramDomainesThemes || {});
    const { careerPaths } = useSelector(state => state.careerPath || { careerPaths: [] });
    const { employeeCareerPaths } = useSelector(state => state.employeeCareerPath || { employeeCareerPaths: [] });
    const { list: seuilsPassage } = useSelector(state => state.seuilPassage || { list: [] });
    const { jobs = [] } = useSelector(state => state.jobs || { jobs: [] });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [roadmapSearch, setRoadmapSearch] = useState("");
    const [filterStatus] = useState("ALL");
    const [activeTab, setActiveTab] = useState("radar"); // "radar" or "4box"
    const [globalActiveTab, setGlobalActiveTab] = useState("radars"); // "radars", "4box", "plans"
    const [roadmapSubTab, setRoadmapSubTab] = useState("career");
    const [currentRadarIndex, setCurrentRadarIndex] = useState(0);
    const [planModalUser, setPlanModalUser] = useState(null);
    const [detailModalUser, setDetailModalUser] = useState(null);
    const [roadmapModalUser, setRoadmapModalUser] = useState(null);
    const [radarFilters, setRadarFilters] = useState({
        entity: '',
        poste: '',
        domaine: '',
        theme: '',
        competence: '',
    });

    useEffect(() => {
        dispatch(fetchRfInboxRequest());
        dispatch(fetchCampagnesRequest());
        dispatch(fetchAllFormationSkillsRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
        dispatch(getAllCareerPathsRequest());
        dispatch(getAllEmployeeCareerPathsRequest());
        dispatch(getAllJobsRequest());
    }, [dispatch]);

    useEffect(() => {
        if (currentUser?.companyId) {
            dispatch(fetchSeuilPassageRequest(currentUser.companyId));
        }
    }, [dispatch, currentUser?.companyId]);

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true && isSkillsCampaign(c)), 
    [campagnesList]);

    // Initialisation par défaut de la campagne (Désactivé pour le RH pour voir tout par défaut)
    /*
    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);
    */

    const globalList = useMemo(() => {
        return (inboxRf || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.trim().split(' ').map(n => n ? n[0] : "").join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName || "Collaborateur Inconnu",
            matricule: e.matricule || e.employeeCode || e.userEmployeeCode || "",
            poste: e.jobName || e.userEmployeeCode || "Collaborateur",
            color: '#204385'
        }));
    }, [inboxRf]);

    const radarFilterOptions = useMemo(() => {
        const radarOptions = buildRadarFilterOptions(globalList);
        return {
            ...radarOptions,
            ...buildDomainThemeRadarOptions(domaines, themes),
            competences: radarOptions.competences,
        };
    }, [globalList, domaines, themes]);

    const filteredUsers = useMemo(() => {
        return filterUsersByRadarFilters(globalList, { ...radarFilters, search: searchQuery }).filter(u => {
            const uStatut = typeof u.statut === 'string' ? u.statut : u.statut?.name;
            const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
            const matchStatus = filterStatus === "ALL" || uStatut === filterStatus;
            return matchCampagne && matchStatus;
        });
    }, [globalList, radarFilters, searchQuery, selectedCampagneId, filterStatus]);

    const roadmapCollaborators = useMemo(() => {
        const query = normalizeText(roadmapSearch);
        return globalList.filter((user) => {
            const uStatut = typeof user.statut === 'string' ? user.statut : user.statut?.name;
            const matchCampagne = !selectedCampagneId || user.campagneId === parseInt(selectedCampagneId);
            const matchStatus = filterStatus === "ALL" || uStatut === filterStatus;
            const matchSearch = !query || normalizeText(`${user.nom || ''} ${user.userFullName || ''} ${getUserMatricule(user)}`).includes(query);
            return matchCampagne && matchStatus && matchSearch;
        });
    }, [globalList, roadmapSearch, selectedCampagneId, filterStatus]);

    const collaboratorOptions = useMemo(() => filteredUsers.map((user) => ({
        value: user.id?.toString(),
        label: `${user.nom}${getUserMatricule(user) ? ` (${getUserMatricule(user)})` : ''}${user.poste ? ` - ${user.poste}` : ''}`,
    })), [filteredUsers]);

    const handleCollaboratorFilterChange = (value) => {
        if (!value) {
            setGlobalActiveTab(toGlobalTab(activeTab));
            setSelectedUser(null);
            return;
        }
        const user = filteredUsers.find((u) => u.id?.toString() === value);
        setActiveTab(toIndividualTab(globalActiveTab));
        setSelectedUser(user || null);
    };

    useEffect(() => {
        if (selectedUser && !filteredUsers.some((u) => u.id === selectedUser.id)) {
            setGlobalActiveTab(toGlobalTab(activeTab));
            setSelectedUser(null);
        }
    }, [activeTab, filteredUsers, selectedUser]);

    useEffect(() => {
        setCurrentRadarIndex(0);
    }, [selectedCampagneId, searchQuery, filterStatus, radarFilters]);

    // Fetch consolidated 360 scores when user or campaign changes
    // Récupération des résultats consolidés (360) pour le collaborateur sélectionné ou celui affiché en vue générale
    useEffect(() => {
        const userToFetch = selectedUser || (filteredUsers && filteredUsers[currentRadarIndex]);
        if (userToFetch && selectedCampagneId) {
            dispatch(fetchConsolidatedResultsRequest(userToFetch.userId || userToFetch.id, selectedCampagneId));
        }
    }, [dispatch, selectedUser, filteredUsers, currentRadarIndex, selectedCampagneId]);

    const radarData = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];

        return filterRadarData(selectedUser.niveaux.map(n => {
            const consolidated = consolidatedResults.find(cr => cr.skillId === n.skillId);
            return toRadarSkill(n, {
                consolidated: consolidated ? consolidated.consolidatedScore : null,
            });
        }), radarFilters);
    }, [selectedUser, consolidatedResults, radarFilters]);

    const openRoadmapModal = (user) => {
        setDetailModalUser(null);
        setRoadmapModalUser(user);
    };

    const selectRoadmapUser = (user) => {
        setSelectedUser(user);
        setActiveTab('roadmap');
        setRoadmapSubTab('career');
        setRoadmapSearch('');
    };

    const showRoadmapPicker = !selectedUser && globalActiveTab === 'roadmaps';
    const hideRadarFilters = showRoadmapPicker || (selectedUser && activeTab === 'roadmap');

    return (
        <div className="eval-page eval-radar-page eval-rf-radar-page">
            <div className="eval-header eval-radar-header">
                {/*<div>*/}
                {/*    <h1>États des compétences</h1>*/}
                {/*    <p>Visualisez les profils de compétences des collaborateurs à travers toute l'organisation.</p>*/}
                {/*</div>*/}
                <label className="eval-rf-campaign-top">
                    <span>Campagne</span>
                    <select value={selectedCampagneId} onChange={(event) => setSelectedCampagneId(event.target.value)}>
                        <option value="">Toutes les campagnes</option>
                        {(activeCampagnes || []).map((campaign) => (
                            <option key={campaign.id} value={campaign.id?.toString()}>
                                {campaign.nom || campaign.name || 'Campagne'}{campaign.anneeCampagne ? ` (${campaign.anneeCampagne})` : ''}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="eval-radar-tabs-bar">
                {!selectedUser ? (
                    <div className="eval-radar-top-tabs">
                        <button className={globalActiveTab === 'radars' ? 'active' : ''} onClick={() => setGlobalActiveTab('radars')}><RadarIcon size={14} /> Radar</button>
                        <button className={globalActiveTab === '4box' ? 'active' : ''} onClick={() => setGlobalActiveTab('4box')}><LayoutGrid size={14} /> 4-Box</button>
                        <button className={globalActiveTab === 'plans' ? 'active' : ''} onClick={() => setGlobalActiveTab('plans')}><GraduationCap size={14} /> Plans de formation</button>
                        <button className={globalActiveTab === 'roadmaps' ? 'active' : ''} onClick={() => setGlobalActiveTab('roadmaps')}><GitBranch size={14} /> Roadmap</button>
                    </div>
                ) : (
                    <div className="eval-radar-top-tabs">
                        <button className={activeTab === 'radar' ? 'active' : ''} onClick={() => setActiveTab('radar')}><RadarIcon size={14} /> Radar</button>
                        <button className={activeTab === '4box' ? 'active' : ''} onClick={() => setActiveTab('4box')}><LayoutGrid size={14} /> 4-Box</button>
                        <button className={activeTab === 'plan' ? 'active' : ''} onClick={() => setActiveTab('plan')}><GraduationCap size={14} /> Plans de formation</button>
                        <button className={activeTab === 'roadmap' ? 'active' : ''} onClick={() => setActiveTab('roadmap')}><GitBranch size={14} /> Roadmap</button>
                    </div>
                )}
            </div>

            {!hideRadarFilters && (
                <RadarFiltersPanel
                    filters={radarFilters}
                    setFilters={setRadarFilters}
                    options={radarFilterOptions}
                    collaboratorValue={selectedUser?.id?.toString() || ''}
                    onCollaboratorChange={handleCollaboratorFilterChange}
                    collaboratorOptions={collaboratorOptions}
                />
            )}

            <div className="eval-layout">
                {false && <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input 
                                type="text" 
                                className="eval-input" 
                                placeholder="Rechercher un collaborateur..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="eval-list">
                        <div 
                            className={`eval-user-card ${selectedUser === null ? 'active' : ''}`} 
                            onClick={() => setSelectedUser(null)}
                            style={{ borderBottom: '2px solid #e2e8f0', marginBottom: '10px', backgroundColor: selectedUser === null ? '#f1f5f9' : 'transparent' }}
                        >
                            <div className="eval-avatar" style={{ backgroundColor: '#94a3b8' }}><Users size={20} color="white" /></div>
                            <div className="eval-user-info">
                                <div className="eval-user-name" style={{ fontWeight: '600' }}>Vue Générale</div>
                                {/*<div className="eval-user-sub">Dashboard RH</div>*/}
                            </div>
                        </div>

                        {loading && globalList.length === 0 ? (
                            <div className="eval-empty-small">Chargement...</div>
                        ) : filteredUsers.length > 0 ? filteredUsers.map(u => (
                            <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                <div className="eval-user-info">
                                    <div className="eval-user-name">{u.nom}</div>
                                    <div className="eval-user-sub">{u.poste} · {u.campagneNom}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="eval-empty-small">Aucun collaborateur trouvé</div>
                        )}
                    </div>
                </aside>}

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                {/*<div className="eval-section-title" style={{ justifyContent: 'center' }}>*/}
                                {/*    <Users size={18} /> Vue Générale (Dashboard RH)*/}
                                {/*</div>*/}
                                
                                <div className="eval-content-tabs">
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === 'radars' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('radars')}
                                    >
                                        <RadarIcon size={14} /> Radars Globaux
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === '4box' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('4box')}
                                    >
                                        <LayoutGrid size={14} /> Matrice 4-Box
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === 'plans' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('plans')}
                                    >
                                        <GraduationCap size={14} /> Plans de Formation
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${globalActiveTab === 'roadmaps' ? 'active' : ''}`}
                                        onClick={() => setGlobalActiveTab('roadmaps')}
                                    >
                                        <GitBranch size={14} /> Roadmaps Globales
                                    </button>
                                </div>

                                {globalActiveTab === 'radars' ? (
                                    <div className="eval-general-radars">
                                        {filteredUsers.length > 0 ? (() => {
                                            const currentUser = filteredUsers[currentRadarIndex];
                                            if (!currentUser) return null;
                                            
                                            // Get radarData for currentUser
                                            const cRadarData = currentUser.niveaux ? filterRadarData(currentUser.niveaux.map(n => {
                                                const consolidated = (consolidatedResults || []).find(cr => cr.skillId === n.skillId);
                                                return toRadarSkill(n, {
                                                    consolidated: consolidated ? consolidated.consolidatedScore : null,
                                                });
                                            }), radarFilters) : [];

                                            return (
                                                <div style={{ position: 'relative', marginTop: '20px' }}>

                                                    <div className="eval-rf-radar-nav">
                                                        <button 
                                                            onClick={() => setCurrentRadarIndex(prev => prev > 0 ? prev - 1 : filteredUsers.length - 1)}
                                                            className="eval-rf-radar-nav-btn"
                                                        >
                                                            <ChevronLeft size={16} />
                                                        </button>
                                                        
                                                        <div className="eval-rf-radar-nav-count">
                                                            {currentRadarIndex + 1} / {filteredUsers.length}
                                                        </div>

                                                        <button 
                                                            onClick={() => setCurrentRadarIndex(prev => prev < filteredUsers.length - 1 ? prev + 1 : 0)}
                                                            className="eval-rf-radar-nav-btn"
                                                        >
                                                            <ChevronRight size={16} />
                                                        </button>
                                                    </div>
                                                    
                                                    {cRadarData.length > 0 ? (
                                                        <SkillRadar data={cRadarData} compact title={currentUser.nom} subtitle={currentUser.poste} />
                                                    ) : (
                                                        <div className="eval-empty-small" style={{ margin: '40px 0' }}>Aucune donnée radar pour ce collaborateur.</div>
                                                    )}
                                                    
                                                </div>
                                            );
                                        })() : (
                                            <div className="eval-empty-small">Aucun collaborateur à afficher.</div>
                                        )}
                                    </div>
                                ) : globalActiveTab === '4box' ? (
                                    <div style={{ marginTop: '20px' }}>
                                        <Team4Box
                                            teamData={filteredUsers}
                                            onUserSelect={setDetailModalUser}
                                            compact
                                        />
                                    </div>
                                ) : globalActiveTab === 'roadmaps' ? (
                                    <>
                                        <div className="eval-rf-roadmap-picker">
                                            <div className="eval-rf-roadmap-search">
                                                <Search size={16} />
                                                <input
                                                    type="text"
                                                    value={roadmapSearch}
                                                    onChange={(event) => setRoadmapSearch(event.target.value)}
                                                    placeholder="Rechercher par matricule ou nom"
                                                />
                                            </div>
                                            {roadmapSearch.trim() && (
                                                <div className="eval-rf-roadmap-results">
                                                    {roadmapCollaborators.length > 0 ? roadmapCollaborators.map((user) => (
                                                        <button key={user.id} type="button" onClick={() => selectRoadmapUser(user)}>
                                                            <span className="eval-rf-roadmap-result-name">{user.nom}</span>
                                                            <span className="eval-rf-roadmap-result-meta">
                                                                {getUserMatricule(user) || 'Sans matricule'} - {getUserPoste(user) || 'Poste non renseigne'}
                                                            </span>
                                                        </button>
                                                    )) : (
                                                        <div className="eval-rf-roadmap-no-result">Aucun collaborateur trouve.</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <RoadmapSelectCollaboratorMessage />
                                    </>
                                ) : (
                                    <div className="eval-general-plans">
                                        <div style={{ overflowX: 'auto', marginTop: '20px' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600' }}>Collaborateur</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600' }}>Poste</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>A besoin de formation</th>
                                                        <th style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredUsers.map(u => {
                                                        const cRadarData = u.niveaux ? filterRadarData(u.niveaux.map(n => toRadarSkill(n)), radarFilters) : [];
                                                        const hasGap = cRadarData.some(s => s.requis > s.obtenu);
                                                        
                                                        return (
                                                            <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '12px 8px', fontWeight: '500', color: '#0f172a' }}>{u.nom}</td>
                                                                <td style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px' }}>{u.poste}</td>
                                                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                                    <span style={{ 
                                                                        padding: '4px 8px', 
                                                                        borderRadius: '4px', 
                                                                        fontSize: '12px', 
                                                                        fontWeight: '500', 
                                                                        backgroundColor: hasGap ? '#fee2e2' : '#dcfce7', 
                                                                        color: hasGap ? '#dc2626' : '#16a34a' 
                                                                    }}>
                                                                        {hasGap ? 'Oui' : 'Non'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                                                    <button 
                                                                        onClick={() => setPlanModalUser(u)}
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                                                                        title="Voir le plan de formation"
                                                                    >
                                                                        <Eye size={18} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {filteredUsers.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>Aucun collaborateur trouvé.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : selectedUser ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                <div className="eval-section-title" style={{ justifyContent: 'center' }}>
                                    {activeTab === 'radar' ? <RadarIcon size={18} /> : activeTab === '4box' ? <LayoutGrid size={18} /> : activeTab === 'plan' ? <GraduationCap size={18} /> : <GitBranch size={18} />}
                                    {activeTab === 'radar' ? "Radar Individuel" : activeTab === '4box' ? "Matrice 4-Box Stratégique" : activeTab === 'roadmap' ? "Parcours de carrière" : "Plan de Formation"} - {selectedUser.nom}
                                </div>

                                {/* Tabs Switcher */}
                                <div className="eval-content-tabs">
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'radar' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('radar')}
                                    >
                                        <RadarIcon size={14} /> Radar
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === '4box' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('4box')}
                                    >
                                        <LayoutGrid size={14} /> Matrice 4-Box
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'plan' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('plan')}
                                    >
                                        <GraduationCap size={14} /> Plan de Formation
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'roadmap' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('roadmap')}
                                    >
                                        <GitBranch size={14} /> Roadmap
                                    </button>
                                </div>

                                {activeTab === 'radar' ? (
                                    <>
                                        {radarData.length > 0 ? <SkillRadar data={radarData} compact title={selectedUser.nom} subtitle={selectedUser.poste} /> : <div className="eval-empty-small" style={{ margin: '40px 0' }}>Aucune donnÃ©e radar pour ce collaborateur.</div>}
                                        {/*<div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>*/}
                                        {/*    Le polygone <strong>émeraude</strong> représente le consensus 360° (Consolidé).<br/>*/}
                                        {/*    Le polygone <strong>gris</strong> représente l'auto-évaluation.<br/>*/}
                                        {/*    La bordure <strong>corail</strong> représente le niveau attendu par le poste.*/}
                                        {/*</div>*/}
                                    </>
                                ) : activeTab === '4box' ? (
                                    <Team4Box teamData={[selectedUser]} onUserSelect={setDetailModalUser} compact />
                                ) : activeTab === 'plan' ? (
                                    <UserTrainingPlanView radarData={radarData} allMappings={allMappings} />
                                ) : (
                                    <div className="eval-rf-roadmap-subview">
                                        <div className="eval-rf-roadmap-subtabs">
                                            <button
                                                type="button"
                                                className={roadmapSubTab === 'career' ? 'active' : ''}
                                                onClick={() => setRoadmapSubTab('career')}
                                            >
                                                Parcours de carrière
                                            </button>
                                            <button
                                                type="button"
                                                className={roadmapSubTab === 'ai' ? 'active' : ''}
                                                onClick={() => setRoadmapSubTab('ai')}
                                            >
                                                Analyse IA
                                            </button>
                                        </div>
                                        {roadmapSubTab === 'career' ? (
                                            <CollaboratorRoadmapView selectedUser={selectedUser} careerPaths={careerPaths} employeeCareerPaths={employeeCareerPaths} />
                                        ) : (
                                            <RoadmapAiAnalysisView
                                                selectedUser={selectedUser}
                                                careerPaths={careerPaths}
                                                employeeCareerPaths={employeeCareerPaths}
                                                jobs={jobs}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Données indisponibles</h3>
                            <p>Aucune évaluation n'a été trouvée pour ce collaborateur dans les filtres sélectionnés.</p>
                        </div>
                    )}
                </main>
            </div>

            <CollaboratorDetailModal
                user={detailModalUser}
                onClose={() => setDetailModalUser(null)}
                onShowRoadmap={openRoadmapModal}
            />

            <CollaboratorRoadmapModal
                user={roadmapModalUser}
                careerPaths={careerPaths}
                employeeCareerPaths={employeeCareerPaths}
                onClose={() => setRoadmapModalUser(null)}
            />

            {/* Plan de Formation Modal */}
            {planModalUser && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
                     onMouseDown={(event) => event.target === event.currentTarget && setPlanModalUser(null)}
                >
                    <div style={{
                        backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '800px', 
                        maxHeight: '90vh', overflowY: 'auto', padding: '24px', position: 'relative',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <button 
                            onClick={() => setPlanModalUser(null)}
                            style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                        >
                            <X size={24} color="#64748b" />
                        </button>
                        
                        <div style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                                Plan de formation - {planModalUser.nom}
                            </h2>
                            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: '14px' }}>{planModalUser.poste}</p>
                        </div>

                        <UserTrainingPlanView 
                            radarData={planModalUser.niveaux ? filterRadarData(planModalUser.niveaux.map(n => toRadarSkill(n)), radarFilters) : []} 
                            allMappings={allMappings} 
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
